import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';

import { ClientSubNav } from '../components/ClientSubNav/ClientSubNav';
import { findClientTab } from '../components/Sidebar/navItems';
import { Sidebar } from '../components/Sidebar/Sidebar';
import { TopBar } from '../components/TopBar/TopBar';
import { CommandPalette } from './CommandPalette';
import { useTeamMember } from './useTeamMember';
import { useOrg } from './useOrg';
import { useUserProfile } from './useUserProfile';
import styles from './Shell.module.css';

interface Props {
  session: Session;
}

export function Shell({ session }: Props) {
  const { data: teamMember } = useTeamMember(session);
  const { data: org } = useOrg(teamMember?.organization_id ?? null);
  const { data: profile } = useUserProfile(session.user.id);
  const { pathname } = useLocation();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openPalette = useCallback(() => { setPaletteOpen(true); }, []);
  const closePalette = useCallback(() => { setPaletteOpen(false); }, []);
  const toggleSidebar = useCallback(() => { setSidebarOpen((v) => !v); }, []);
  const closeSidebar = useCallback(() => { setSidebarOpen(false); }, []);

  // Global Cmd/Ctrl+K to open the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, []);

  return (
    <div className={styles.shell}>
      <TopBar
        session={session}
        fullName={profile?.full_name ?? null}
        role={teamMember?.role ?? null}
        onSidebarToggle={toggleSidebar}
        onPaletteOpen={openPalette}
      />
      <Sidebar
        orgName={org?.name ?? null}
        open={sidebarOpen}
        onNavigate={closeSidebar}
      />
      {sidebarOpen ? (
        <div
          className={styles.backdrop}
          onClick={closeSidebar}
          role="presentation"
        />
      ) : null}
      <main className={styles.main}>
        {findClientTab(pathname) ? <ClientSubNav /> : null}
        <Outlet />
      </main>
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
}
